"use client"

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react-safe"
import { useRouter } from "next/navigation"
import { Toaster } from "../ui/sonner"

type NavigationContextType = {
  navigateTo: (path: string) => void
  isNavigating: boolean
}

const NavigationContext = createContext<NavigationContextType>({
  navigateTo: () => { },
  isNavigating: false,
})

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [isNavigating, setIsNavigating] = useState(false)
  const router = useRouter()

  // Memoize navigateTo to maintain stable reference
  const navigateTo = useCallback((path: string) => {
    setIsNavigating((current) => {
      if (current) return current // Already navigating, skip

      // Schedule navigation asynchronously
      setTimeout(() => {
        router.push(path)
        // Reset after navigation completes
        setTimeout(() => setIsNavigating(false), 300)
      }, 0)

      return true
    })
  }, [router])

  // Memoize context value to prevent consumer re-renders
  const value = useMemo(
    () => ({ navigateTo, isNavigating }),
    [navigateTo, isNavigating]
  )

  return (
    <NavigationContext.Provider value={value}>
      <Toaster />
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = () => useContext(NavigationContext)
